import * as React from 'react';
import { textColorClasses as sharedTextColorClasses } from '../../shared/styleTokens';

/**
 * Allowed HTML elements for the Text component
 */
type TextElement = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label';

/**
 * Type scale variants based on the design system
 * - xs: 12px/16px - Captions, timestamps, helper text
 * - sm: 13px/18px - Secondary labels, table cells
 * - base: 15px/22px - Body text, form inputs
 * - lg: 18px/26px - Section labels, card titles
 * - xl: 22px/28px - Page section headings
 * - 2xl: 28px/34px - Page titles
 * - 3xl: 35px/40px - Dashboard metrics
 * - 4xl: 44px/48px - Hero numbers, KPIs
 */
type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

/**
 * Font weight variants
 */
type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold';

/**
 * Color variants for text
 */
type TextColor = 'default' | 'primary' | 'secondary' | 'muted' | 'error' | 'success';

/**
 * Base props shared across all Text elements
 */
interface TextBaseProps {
  /**
   * The HTML element to render
   * @default 'p'
   */
  as?: TextElement;
  /**
   * Size of the text following the type scale
   * @default 'base'
   */
  size?: TextSize;
  /**
   * Font weight
   * @default 'normal'
   */
  weight?: TextWeight;
  /**
   * Text color variant
   * @default 'default'
   */
  color?: TextColor;
  /**
   * Whether to truncate text with ellipsis (single line)
   * @default false
   */
  truncate?: boolean;
  /**
   * Number of lines to clamp before truncating
   * When set, enables multi-line truncation
   */
  lineClamp?: number;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * The content to display
   */
  children: React.ReactNode;
}

/**
 * TextProps extends HTML attributes to support all native element props
 * Including label-specific attributes like htmlFor
 */
export type TextProps = TextBaseProps &
  Omit<React.HTMLAttributes<HTMLElement>, keyof TextBaseProps> &
  Partial<Pick<React.LabelHTMLAttributes<HTMLLabelElement>, 'htmlFor'>>;

/**
 * Size classes mapping based on the design system type scale
 * Uses custom sizing to match spec exactly:
 * - xs: 12px / 16px line-height
 * - sm: 13px / 18px line-height (custom, not standard Tailwind)
 * - base: 15px / 22px line-height (custom)
 * - lg: 18px / 26px line-height (custom)
 * - xl: 22px / 28px line-height (custom)
 * - 2xl: 28px / 34px line-height (custom)
 * - 3xl: 35px / 40px line-height (custom)
 * - 4xl: 44px / 48px line-height (custom)
 */
const sizeClasses: Record<TextSize, string> = {
  xs: 'text-[12px] leading-[16px]',
  sm: 'text-[13px] leading-[18px]',
  base: 'text-[15px] leading-[22px]',
  lg: 'text-[18px] leading-[26px]',
  xl: 'text-[22px] leading-[28px]',
  '2xl': 'text-[28px] leading-[34px]',
  '3xl': 'text-[35px] leading-[40px]',
  '4xl': 'text-[44px] leading-[48px]',
};

/**
 * Weight classes mapping
 */
const weightClasses: Record<TextWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

/**
 * Color classes mapping based on the design system
 * Using shared tokens from the design spec for consistency
 */
const colorClasses: Record<TextColor, string> = sharedTextColorClasses;

/**
 * Text component for rendering typography with consistent styling.
 *
 * Supports the full type scale from the design system, semantic HTML elements,
 * weight variants, color variants, and text truncation/clamping.
 *
 * @example
 * // Basic usage
 * <Text>Body text content</Text>
 *
 * @example
 * // Heading
 * <Text as="h1" size="2xl" weight="semibold">Page Title</Text>
 *
 * @example
 * // Truncated text
 * <Text truncate>Very long text that will be truncated...</Text>
 *
 * @example
 * // Line clamping
 * <Text lineClamp={2}>Multi-line text that will be clamped to 2 lines...</Text>
 */
const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      as: Component = 'p',
      size = 'base',
      weight = 'normal',
      color = 'default',
      truncate = false,
      lineClamp,
      className = '',
      children,
      style,
      ...props
    },
    ref
  ) => {
    // For line-clamp, we need inline styles because Tailwind JIT won't generate
    // arbitrary line-clamp values dynamically
    const lineClampStyle: React.CSSProperties | undefined =
      lineClamp && !truncate
        ? {
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: lineClamp,
            ...style,
          }
        : style;

    // Combine all classes
    const combinedClasses = [
      sizeClasses[size],
      weightClasses[weight],
      colorClasses[color],
      truncate ? 'truncate' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // We need to use type assertion here because the component is dynamic
    // and TypeScript can't infer the correct ref type for all possible elements
    const ElementComponent = Component as React.ElementType;

    return (
      <ElementComponent
        ref={ref}
        className={combinedClasses}
        style={lineClampStyle}
        {...props}
      >
        {children}
      </ElementComponent>
    );
  }
);

Text.displayName = 'Text';

export { Text };
export type { TextElement, TextSize, TextWeight, TextColor };
